import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreHorizontal } from 'lucide-react'
import { useMutation } from '@apollo/client'
import { toast } from 'sonner'
import { UPDATE_ORDER_ITEM_METADATA, DELETE_ORDER_ITEM } from '../OrderQueries'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { ConfigurationDrawer } from './ConfigurationDrawer'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Order } from '@/types/order'
import { formatDate } from '@/utils/dateUtils'
import { calculateOrderVolume } from '@/utils/orderUtils'
import { JsonMetadataViewer } from '@/components/shared/JsonMetadataViewer'

interface CartItemsTableProps {
  orderItems: Order['order_items']
  totalPriceConfirmed?: number
}

export function CartItemsTable({
  orderItems,
  totalPriceConfirmed,
}: CartItemsTableProps) {
  const navigate = useNavigate()
  const openConfigDrawer = useOpenDrawer(ConfigurationDrawer)
  const [selectedMetadata, setSelectedMetadata] = useState<Record<
    string,
    any
  > | null>(null)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedMetadataText, setEditedMetadataText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  const [updateOrderItemMetadata, { loading: isSaving }] = useMutation(
    UPDATE_ORDER_ITEM_METADATA,
    {
      onCompleted: () => {
        toast.success('Metadata updated successfully')
        setIsEditMode(false)
      },
      onError: error => {
        toast.error(`Failed to update metadata: ${error.message}`)
      },
      refetchQueries: ['GET_ORDER', 'GET_ORDER_BY_ID'],
    }
  )

  const [deleteOrderItem, { loading: isDeleting }] = useMutation(
    DELETE_ORDER_ITEM,
    {
      onCompleted: () => {
        toast.success('Order item deleted successfully')
      },
      onError: error => {
        toast.error(`Failed to delete order item: ${error.message}`)
      },
      refetchQueries: ['GET_ORDER', 'GET_ORDER_BY_ID'],
    }
  )

  const handleDeleteOrderItem = async (itemId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this order item? This action cannot be undone.'
      )
    ) {
      return
    }

    await deleteOrderItem({
      variables: { id: parseInt(itemId) },
    })
  }

  const handleMetadataClick = (
    metadata: Record<string, any> | null,
    itemId: string
  ) => {
    setSelectedMetadata(metadata)
    setSelectedItemId(itemId)
    setEditedMetadataText(JSON.stringify(metadata || {}, null, 2))
    setIsMetadataDialogOpen(true)
    setIsEditMode(false)
    setJsonError(null)
  }

  const handleEditClick = () => {
    setIsEditMode(true)
    setEditedMetadataText(JSON.stringify(selectedMetadata || {}, null, 2))
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditedMetadataText(JSON.stringify(selectedMetadata || {}, null, 2))
    setJsonError(null)
  }

  const handleMetadataTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setEditedMetadataText(e.target.value)
    // Validate JSON on change
    try {
      JSON.parse(e.target.value)
      setJsonError(null)
    } catch (error) {
      setJsonError('Invalid JSON format')
    }
  }

  const handleSaveMetadata = async () => {
    try {
      const parsedMetadata = JSON.parse(editedMetadataText)
      await updateOrderItemMetadata({
        variables: {
          id: Number(selectedItemId),
          metadata: parsedMetadata,
        },
      })
      setSelectedMetadata(parsedMetadata)
    } catch (error) {
      setJsonError('Invalid JSON format')
    }
  }

  console.log('orderItems', orderItems)

  const orderItemsSorted = [...orderItems].sort((a, b) => {
    // First, compare by order_index if both are defined and not null
    if (a.order_index != null && b.order_index != null) {
      return a.order_index - b.order_index
    }

    // If only a has order_index, it comes first
    if (a.order_index != null && b.order_index == null) {
      return -1
    }

    // If only b has order_index, it comes first
    if (a.order_index == null && b.order_index != null) {
      return 1
    }

    // If neither has order_index, maintain original order (stable sort)
    return 0
  })

  const hasMetadata =
    selectedMetadata && Object.keys(selectedMetadata).length > 0

  return (
    <>
      <Table className="text-gray-500 text-xs leading-6">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Image</TableHead>
            <TableHead>Product Name</TableHead>
            <TableHead className="text-right">Reference</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead className="text-right">Volume</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orderItemsSorted.map((item, index) => {
            const container = item.cart_item.product_container

            // Handle case where product container is null
            if (!container) {
              return (
                <TableRow key={index}>
                  <TableCell className="p-2">
                    <div className="w-[60px] h-[60px] bg-gray-100 rounded-md flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium p-2">
                    <span className="text-gray-400">Deleted Product</span>
                  </TableCell>
                  <TableCell className="text-right p-2">
                    <span className="text-gray-400">{item.reference}</span>
                  </TableCell>
                  <TableCell className="text-right p-2">
                    {item.quantity || 1}
                  </TableCell>
                  <TableCell className="text-right p-2">
                    {item.volume?.toFixed(2)} m<sup>3</sup>
                  </TableCell>
                  <TableCell className="text-right p-2">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem disabled>
                          View Product
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleMetadataClick(item.metadata, item.id)}
                        >
                          Metadata
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteOrderItem(item.id)}
                          className="text-red-600"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            }

            // For existing products
            const profiles =
              container.type === 'SINGLE_PRODUCT'
                ? container.single_product?.product_profiles
                : container.advanced_product?.advanced_product_profiles
            const images =
              container.type === 'SINGLE_PRODUCT'
                ? container.single_product?.images
                : container.advanced_product?.images
            const productName = profiles?.[0]?.name || 'Unnamed Product'
            const thumbnail = images?.[0]?.src_thumbnail

            return (
              <TableRow key={index}>
                <TableCell className="p-2">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={productName}
                      className="w-[60px] h-[60px] object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-[60px] h-[60px] bg-gray-100 rounded-md flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium p-2">
                  <div className="flex items-center gap-2">
                    <span>{productName}</span>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-xs text-blue-500"
                      onClick={() =>
                        openConfigDrawer({
                          productId: container.id,
                          cartItemFabrics: item.cart_item.cartItemFabrics,
                          productName,
                          fabricCombination: item.cart_item.fabricCombination,
                          selected_sofa_combinations:
                            item.cart_item.selected_sofa_combinations,
                          additional_components:
                            item.cart_item.additional_components,
                        })
                      }
                    >
                      Configuration
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-right p-2">
                  {item.reference}
                </TableCell>
                <TableCell className="text-right p-2">
                  {item.quantity || 1}
                </TableCell>
                <TableCell className="text-right p-2">
                  {item.volume?.toFixed(2)} m<sup>3</sup>
                </TableCell>
                <TableCell className="text-right p-2">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => navigate(`/products/${container.id}`)}
                      >
                        View Product
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleMetadataClick(item.metadata, item.id)}
                      >
                        Metadata
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteOrderItem(item.id)}
                        className="text-red-600"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
          <TableRow className="border-t-2">
            <TableCell colSpan={4} className="text-right font-medium p-2">
              Total:
            </TableCell>
            <TableCell className="text-right font-medium p-2">
              {calculateOrderVolume(orderItems).toFixed(2)} m<sup>3</sup>
            </TableCell>
            <TableCell className="p-2" />
          </TableRow>
          {totalPriceConfirmed !== undefined && (
            <TableRow>
              <TableCell colSpan={4} className="text-right font-medium p-2">
                Confirmed Value:
              </TableCell>
              <TableCell className="text-right font-medium p-2">
                {totalPriceConfirmed
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'EUR',
                    }).format(totalPriceConfirmed)
                  : '-'}
              </TableCell>
              <TableCell className="p-2" />
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={isMetadataDialogOpen}
        onOpenChange={setIsMetadataDialogOpen}
      >
        <DialogContent className="max-w-4xl w-full max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Order Item Metadata</DialogTitle>
          </DialogHeader>
          <div className="flex-grow overflow-hidden">
            {isEditMode ? (
              <div className="flex flex-col h-full gap-2">
                <Textarea
                  value={editedMetadataText}
                  onChange={handleMetadataTextChange}
                  className="font-mono text-sm h-[500px] resize-none"
                  placeholder="Enter JSON metadata..."
                />
                {jsonError && (
                  <p className="text-sm text-red-500">{jsonError}</p>
                )}
              </div>
            ) : hasMetadata ? (
              <JsonMetadataViewer data={selectedMetadata} height="500px" />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>No metadata available for this order item.</p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            {isEditMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveMetadata}
                  disabled={!!jsonError || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button onClick={handleEditClick}>Edit</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
